import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Scale, BookOpen, Sparkles, Users, FileText, Calendar, FolderOpen, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: "",
    user_type: "lawyer",
    specialization: "",
    license_number: "",
    years_experience: ""
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      toast.success("تم تسجيل الدخول بنجاح");
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error("خطأ في تسجيل الدخول: " + (error.response?.data?.detail || "تحقق من البيانات"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...registerData };
      if (registerData.user_type === "citizen") {
        delete payload.specialization;
        delete payload.license_number;
        delete payload.years_experience;
      } else if (registerData.user_type === "law_student") {
        delete payload.license_number;
        payload.years_experience = 0;
      } else {
        payload.years_experience = parseInt(payload.years_experience) || 0;
      }
      
      const response = await axios.post(`${API}/auth/register`, payload);
      toast.success("تم إنشاء الحساب بنجاح");
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error("خطأ في إنشاء الحساب: " + (error.response?.data?.detail || "حاول مرة أخرى"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e3a8a' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative container mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="flex items-center justify-center mb-6">
              <img src="/logo.jpeg" alt="Al-Tamkeen Logo" className="w-24 h-24 rounded-2xl object-cover shadow-lg" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-4">
              التمكين
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 font-light mb-2">
              Al-Tamkeen
            </p>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              منصة رقمية وطنية لتمكين المحامين والحقوقيين في العراق
            </p>
          </div>

          {/* Company Banner */}
          <div className="mb-12 animate-fade-in">
            <div className="rounded-2xl overflow-hidden shadow-2xl mx-auto max-w-4xl">
              <img 
                src="/company-banner.jpeg" 
                alt="شركة التمكين للاستشارات والخدمات القانونية" 
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.1s' }}>
              <Sparkles className="w-10 h-10 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">بحت قانوني ذكي</h3>
              <p className="text-slate-600">محرك بحث مدعوم بالذكاء الاصطناعي للإجابة على الأسئلة القانونية</p>
            </div>
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.2s' }}>
              <FileText className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">منشئ العقود</h3>
              <p className="text-slate-600">توليد عقود ومذكرات قانونية احترافية بضغطة زر</p>
            </div>
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.3s' }}>
              <Briefcase className="w-10 h-10 text-green-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">إدارة القضايا المتقدمة</h3>
              <p className="text-slate-600">نظام متكامل لإدارة القضايا مع أرشفة المستندات المدمجة</p>
            </div>
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.4s' }}>
              <Calendar className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">مفكرة ذكية</h3>
              <p className="text-slate-600">إدارة القضايا والمواعيد مع تنبيهات تلقائية</p>
            </div>
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.5s' }}>
              <Users className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">اسأل محامي</h3>
              <p className="text-slate-600">منصة تربط المواطنين بالمحامين المختصين</p>
            </div>
            <div className="glass-effect rounded-xl p-6 animate-slide-in" style={{ animationDelay: '0.6s' }}>
              <Scale className="w-10 h-10 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">تحليل ذكي</h3>
              <p className="text-slate-600">تحليل القضايا وتقديم توصيات استراتيجية</p>
            </div>
          </div>

          {/* Auth Card */}
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-2xl border-0" data-testid="auth-card">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl font-bold text-slate-900">البدء</CardTitle>
                <CardDescription className="text-base">سجل دخولك أو أنشئ حساباً جديداً</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="login" data-testid="login-tab">تسجيل الدخول</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab">إنشاء حساب</TabsTrigger>
                  </TabsList>

                  {/* Login Tab */}
                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">البريد الإلكتروني</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="example@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">كلمة المرور</Label>
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                        {loading ? "جاري الدخول..." : "تسجيل الدخول"}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Register Tab */}
                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                      <div className="space-y-2">
                        <Label htmlFor="register-name">الاسم الكامل</Label>
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="مثال: أحمد محمد"
                          value={registerData.full_name}
                          onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-email">البريد الإلكتروني</Label>
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="example@email.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-password">كلمة المرور</Label>
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-phone">رقم الهاتف</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="07XXXXXXXXX"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          data-testid="register-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-type">نوع الحساب</Label>
                        <Select
                          value={registerData.user_type}
                          onValueChange={(value) => setRegisterData({ ...registerData, user_type: value })}
                        >
                          <SelectTrigger id="register-type" data-testid="register-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lawyer">نقابة المحامين</SelectItem>
                            <SelectItem value="jurist">حقوقي</SelectItem>
                            <SelectItem value="law_student">طالب/ة كلية قانون</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(registerData.user_type === "lawyer" || registerData.user_type === "jurist" || registerData.user_type === "law_student") && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="register-specialization">التخصص</Label>
                            <Select
                              value={registerData.specialization}
                              onValueChange={(value) => setRegisterData({ ...registerData, specialization: value })}
                            >
                              <SelectTrigger id="register-specialization" data-testid="register-specialization-select">
                                <SelectValue placeholder="اختر التخصص" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="civil">مدني</SelectItem>
                                <SelectItem value="criminal">جنائي</SelectItem>
                                <SelectItem value="commercial">تجاري</SelectItem>
                                <SelectItem value="labor">عمل</SelectItem>
                                <SelectItem value="personal_status">أحوال شخصية</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="register-license">رقم الهوية</Label>
                            <Input
                              id="register-license"
                              type="text"
                              placeholder="رقم إجازة النقابة"
                              value={registerData.license_number}
                              onChange={(e) => setRegisterData({ ...registerData, license_number: e.target.value })}
                              data-testid="register-license-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="register-experience">سنوات الخبرة</Label>
                            <Input
                              id="register-experience"
                              type="number"
                              placeholder="5"
                              value={registerData.years_experience}
                              onChange={(e) => setRegisterData({ ...registerData, years_experience: e.target.value })}
                              data-testid="register-experience-input"
                            />
                          </div>
                        </>
                      )}
                      
                      <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                        {loading ? "جاري إنشاء الحساب..." : "إنشاء حساب"}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © 2025 التمكين - Al-Tamkeen. جميع الحقوق محفوظة
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
